import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function AdminTeam() {
  const [admins, setAdmins] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ email: "", full_name: "", initial_password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/team").then((r) => setAdmins(r.data?.admins || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/admin/team", form);
      toast.success(r.data?.elevated ? "Existing user elevated to admin" : "Admin created");
      setOpenAdd(false);
      setForm({ email: "", full_name: "", initial_password: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}? They will lose all platform-ops access.`)) return;
    try { await api.delete(`/admin/team/${a.id}`); toast.success("Admin removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-team-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-600">// Admin team</div>
          <h1 className="text-3xl font-bold mt-1 text-foreground">Zenith360-ERP admins</h1>
          <div className="text-muted-foreground text-sm mt-1">Everyone with platform-wide access. Every new admin is forced to rotate their password on first sign-in.</div>
        </div>
        <Button onClick={() => setOpenAdd(true)} className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 text-foreground gap-1.5" data-testid="admin-add-btn">
          <Plus className="w-4 h-4" /> Add admin
        </Button>
      </div>

      <Card className="rounded-sm bg-card border-border text-foreground overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left p-3">Admin</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">
                <Users className="w-10 h-10 mx-auto opacity-30" />
                <div className="mt-2">No other admins yet.</div>
              </td></tr>
            )}
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-border/60 hover:bg-muted/40" data-testid={`admin-row-${a.id}`}>
                <td className="p-3">
                  <div className="font-medium">{a.full_name || "—"}</div>
                  {a.must_reset_password && <div className="text-[10px] text-amber-600 font-mono mt-0.5">Pending password reset</div>}
                </td>
                <td className="p-3 font-mono text-xs text-foreground/90">{a.email}</td>
                <td className="p-3">
                  <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-emerald-400/10 text-emerald-600">Active</span>
                </td>
                <td className="p-3 text-[11px] text-muted-foreground">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => removeAdmin(a)}
                          className="rounded-sm text-red-600 hover:text-red-300 hover:bg-red-400/10"
                          data-testid={`admin-remove-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="rounded-sm sm:max-w-md">
          <DialogHeader><DialogTitle>Add Zenith360-ERP admin</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">Full name *</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                     className="rounded-sm mt-1" data-testid="admin-new-name" />
            </div>
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="rounded-sm mt-1" data-testid="admin-new-email" />
              <div className="text-[10px] text-foreground/50 mt-1">If the email exists, that user gets elevated to admin.</div>
            </div>
            <div>
              <Label className="text-xs">Initial password *</Label>
              <Input type="password" required minLength={8} value={form.initial_password}
                     onChange={(e) => setForm({ ...form, initial_password: e.target.value })}
                     className="rounded-sm mt-1" data-testid="admin-new-pw" />
              <div className="text-[10px] text-foreground/50 mt-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin will be forced to change this on first sign-in.</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpenAdd(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="rounded-sm bg-gradient-to-r from-orange-600 to-red-600 text-foreground" data-testid="admin-new-submit">
                {busy ? "Adding…" : "Add admin"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
